/**
 * campaignPipeline.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Affiliate Flow AI — Full Coach → Image → HTML pipeline helpers.
 *
 * Exports:
 *   buildCoachUserPrompt        re-exported from ai-prompts.js
 *   buildPageBuilderUserPrompt  re-exported from ai-prompts.js
 *   runCampaignWizard           main pipeline runner (routes through server)
 *
 * All LLM calls route through /api/wizard/build-page on the server so the
 * Anthropic API key never lives in client-side JS.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export { buildCoachUserPrompt, buildPageBuilderUserPrompt } from './ai-prompts.js';

// ─── Base URL Resolver ────────────────────────────────────────────────────────

/**
 * Resolves the effective API base URL.
 * 
 * In local development, we return empty string '' to leverage the local Vite proxy.
 * In production, we route calls directly to the Google Cloud Run Function URL to 
 * bypass Firebase Hosting's strict 60-second request timeout gateway limit.
 */
function getEffectiveBaseUrl(providedUrl) {
  if (providedUrl) return providedUrl;
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return '';
    }
  }
  return 'https://api-ozjiuaqjoq-uc.a.run.app';
}

// ─── Main Pipeline ────────────────────────────────────────────────────────────

/**
 * Runs the full 3-step Affiliate Flow AI campaign pipeline.
 *
 * Step 1: Coach analysis  → campaign_json   (skipped if formData.campaign_json provided)
 * Step 2: Image generation (Pollinations)  → image_urls
 * Step 3: Page builder    → HTML
 *
 * All LLM work runs server-side via /api/wizard/build-page.
 *
 * Accepts BOTH old wizard field names (name, payout, networkEpc, offer_preview_url)
 * AND new field names (campaignName, payoutAmount, offerEpc, offerPreviewUrl, offerDescription).
 *
 * @param {Object}   formData  - Wizard form state (either field-name shape).
 * @param {string}   [apiKey]  - Anthropic API key (forwarded as x-api-key header).
 * @param {Object}   [opts]
 * @param {Function} [opts.onStatus]   - Status callback string at each step.
 * @param {string}   [opts.baseUrl]    - Server base URL (default '' = same origin).
 *
 * @returns {Promise<{ coachAnalysis: Object, html: string, imageUrls: string[] }>}
 */
export async function fetchOfferContent(offerPreviewUrl, apiKey, baseUrl = '') {
  const headers = {
    'Content-Type': 'application/json',
    ...(apiKey ? { 'x-api-key': apiKey } : {}),
  };

  const effectiveBaseUrl = getEffectiveBaseUrl(baseUrl);
  const response = await fetch(`${effectiveBaseUrl}/api/wizard/fetch-offer`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ offer_preview_url: offerPreviewUrl }),
  });

  if (!response.ok) {
    const errBody = await response.json().catch(() => ({}));
    throw new Error(errBody.error || `Server error ${response.status}`);
  }

  return await response.json();
}

export async function runCampaignPlan(formData, apiKey, opts = {}) {
  const {
    onStatus = () => {},
    baseUrl = '',
  } = opts;

  onStatus('Drafting conversion layout plan...');

  const headers = {
    'Content-Type': 'application/json',
    ...(apiKey ? { 'x-api-key': apiKey } : {}),
  };

  const affiliateLink   = formData.affiliateLink || '';
  const offerPreviewUrl = formData.offer_preview_url || formData.offerPreviewUrl || '';
  const offerDesc       = formData.offer_description || formData.offerDescription || formData.niche || '';
  const network         = formData.network || formData.affiliateNetwork || '';
  const payout          = (formData.payout || formData.payoutAmount || '').toString().replace(/[^0-9.]/g, '');
  const payoutModel     = formData.payoutModel || '';
  const targetGeo       = formData.targetGeo || '';
  const networkEpc      = formData.networkEpc || formData.offerEpc || '';
  const campaignName    = formData.campaignName || formData.name || formData.offerName || '';
  const campaignJson    = formData.campaign_json || null;
  const pageBuilderCtx  = formData.page_builder_context || null;
  const previousPlan    = formData.previous_plan || null;
  const feedback        = formData.feedback || null;

  const payload = {
    campaignName,
    affiliateLink,
    offer_preview_url: offerPreviewUrl,
    offer_description: offerDesc,
    network,
    payout,
    payoutModel,
    targetGeo,
    networkEpc,
    campaign_json: campaignJson,
    page_builder_context: pageBuilderCtx,
    previous_plan: previousPlan,
    feedback,
  };

  const effectiveBaseUrl = getEffectiveBaseUrl(baseUrl);
  const response = await fetch(`${effectiveBaseUrl}/api/wizard/build-plan`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errBody = await response.json().catch(() => ({}));
    throw new Error(errBody.error || `Server error ${response.status}`);
  }

  const result = await response.json();
  onStatus('Plan drafted successfully.');

  return result.plan || '';
}

export async function runCampaignWizard(formData, apiKey, opts = {}) {
  const {
    onStatus = () => {},
    baseUrl = '',
    analyzeOnly = false,
  } = opts;

  onStatus(analyzeOnly ? 'Analyzing offer with AI Coach...' : 'Building your page...');

  const headers = {
    'Content-Type': 'application/json',
    ...(apiKey ? { 'x-api-key': apiKey } : {}),
  };

  // Normalise field names — support both old and new wizard form shapes
  const affiliateLink   = formData.affiliateLink || '';
  const offerPreviewUrl = formData.offer_preview_url || formData.offerPreviewUrl || '';
  const offerDesc       = formData.offer_description || formData.offerDescription || formData.niche || '';
  const network         = formData.network || formData.affiliateNetwork || '';
  const payout          = (formData.payout || formData.payoutAmount || '').toString().replace(/[^0-9.]/g, '');
  const payoutModel     = formData.payoutModel || '';
  const targetGeo       = formData.targetGeo || '';
  const networkEpc      = formData.networkEpc || formData.offerEpc || '';
  
  // Accept pre-fetched scraped content if passed on formData
  const offerFetchedContent = formData.offer_fetched_content || formData.offerFetchedContent || '';

  // If caller passes campaign_json directly on formData, the server skips the Coach step
  const campaignJsonPassthrough = formData.campaign_json || null;

  // If caller passes an approved page_plan (from PageBuilderAgentModal), forward it so the
  // server instructs Claude to follow it instead of generating a generic template.
  const campaignName    = formData.campaignName || formData.name || formData.offerName || '';

  const payload = {
    campaignName,
    affiliateLink,
    offer_preview_url: offerPreviewUrl,
    offer_description: offerDesc,
    network,
    payout,
    payoutModel,
    targetGeo,
    networkEpc,
    ...(offerFetchedContent ? { offer_fetched_content: offerFetchedContent } : {}),
    ...(analyzeOnly ? { analyze_only: true } : {}),
    ...(campaignJsonPassthrough ? { campaign_json: campaignJsonPassthrough } : {}),
    ...(pagePlan ? { page_plan: pagePlan } : {})
  };

  const effectiveBaseUrl = getEffectiveBaseUrl(baseUrl);
  const response = await fetch(`${effectiveBaseUrl}/api/wizard/build-page`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errBody = await response.json().catch(() => ({}));
    throw new Error(errBody.error || `Server error ${response.status}`);
  }

  const result = await response.json();

  if (!analyzeOnly && !result.html) {
    throw new Error('Page builder returned no HTML. Please try again.');
  }

  onStatus(analyzeOnly ? 'Analysis complete!' : 'Done! Your pre-lander is ready.');

  return {
    coachAnalysis: result.campaign_json,   // → populate wizard UI panels
    html: result.html || '',               // → save and preview the page
    imageUrls: result.image_urls || [],    // → store with campaign record
  };
}
